import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  useTheme,
  Box
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import FilterListIcon from '@mui/icons-material/FilterList';
import CartFilter from './CartFilter';

const CartFilterPopover = ({
  open,
  onClose,
  onApply,
  sectionName,
  sectionTitle,
  cartFilterType,
  setCartFilterType,
  selectedCartTypes,
  handleCartTypesChange,
  selectedCarts,
  handleSpecificCartsChange,
  cartsList,
  cartTypeOptions,
  onFilterChange,
  // Section-specific Y-axis percentage props
  isYAxisPercentage,
  handleYAxisPercentageChange,
  availabilityYAxisPercentage,
  handleAvailabilityYAxisPercentageChange,
  onServiceYAxisPercentage,
  handleOnServiceYAxisPercentageChange,
  waitingTimeYAxisPercentage,
  handleWaitingTimeYAxisPercentageChange
}) => {
  const theme = useTheme();

  // Determine which Y-axis percentage value and handler to use based on section
  const getYAxisPercentageValue = () => {
    if (sectionName === 'availability') return availabilityYAxisPercentage || defaultProps.availabilityYAxisPercentage;
    if (sectionName === 'onService') return onServiceYAxisPercentage || defaultProps.onServiceYAxisPercentage;
    if (sectionName === 'waitingTime') return waitingTimeYAxisPercentage || defaultProps.waitingTimeYAxisPercentage;
    return isYAxisPercentage; // fallback for backward compatibility
  };

  const getYAxisPercentageHandler = () => {
    if (sectionName === 'availability') return handleAvailabilityYAxisPercentageChange || defaultProps.handleAvailabilityYAxisPercentageChange;
    if (sectionName === 'onService') return handleOnServiceYAxisPercentageChange || defaultProps.handleOnServiceYAxisPercentageChange;
    if (sectionName === 'waitingTime') return handleWaitingTimeYAxisPercentageChange || defaultProps.handleWaitingTimeYAxisPercentageChange;
    return handleYAxisPercentageChange; // fallback for backward compatibility
  };

  const handleApply = () => {
    if (onFilterChange) {
      onFilterChange();
    }
    if (onApply) {
      onApply();
    }
    onClose();
  };

  // Set default values for the new props to avoid undefined errors
  const defaultProps = {
    availabilityYAxisPercentage: isYAxisPercentage,
    handleAvailabilityYAxisPercentageChange: handleYAxisPercentageChange,
    onServiceYAxisPercentage: isYAxisPercentage,
    handleOnServiceYAxisPercentageChange: handleYAxisPercentageChange,
    waitingTimeYAxisPercentage: isYAxisPercentage,
    handleWaitingTimeYAxisPercentageChange: handleYAxisPercentageChange
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
          background: 'linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)'
        }
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          background: `linear-gradient(135deg, ${theme.palette.primary.main}15 0%, ${theme.palette.primary.main}05 100%)`,
          borderBottom: `2px solid ${theme.palette.primary.main}30`,
          pb: 2
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <FilterListIcon sx={{ color: theme.palette.primary.main }} />
          <span style={{ 
            fontWeight: 700, 
            color: theme.palette.primary.main,
            fontSize: '1.2rem'
          }}>
            {sectionTitle} Filter
          </span>
        </Box>
        <IconButton
          onClick={onClose}
          size="small"
          sx={{
            color: theme.palette.grey[500],
            '&:hover': {
              backgroundColor: theme.palette.grey[100]
            }
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        <CartFilter
          sectionName={sectionName}
          cartFilterType={cartFilterType}
          setCartFilterType={setCartFilterType}
          selectedCartTypes={selectedCartTypes}
          handleCartTypesChange={handleCartTypesChange}
          selectedCarts={selectedCarts}
          handleSpecificCartsChange={handleSpecificCartsChange}
          cartsList={cartsList}
          cartTypeOptions={cartTypeOptions}
          onFilterChange={onFilterChange}
          isYAxisPercentage={getYAxisPercentageValue()}
          handleYAxisPercentageChange={getYAxisPercentageHandler()}
        />
      </DialogContent>

      <DialogActions
        sx={{
          p: 3,
          borderTop: `1px solid ${theme.palette.grey[200]}`,
          background: theme.palette.grey[50],
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}
      >
        <Box>
          <Button
            onClick={onClose}
            variant="outlined"
            sx={{
              borderRadius: 2,
              px: 3,
              textTransform: 'none',
              fontWeight: 600
            }}
          >
            Cancel
          </Button>
        </Box>
        <Box>
          <Button
            onClick={handleApply}
            variant="contained"
            sx={{
              borderRadius: 2,
              px: 3,
              textTransform: 'none',
              fontWeight: 600,
              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
              boxShadow: `0 4px 12px ${theme.palette.primary.main}30`,
              '&:hover': {
                background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
                boxShadow: `0 6px 16px ${theme.palette.primary.main}40`
              }
            }}
          >
            Apply Filter
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default CartFilterPopover;
